import React, { useState, useEffect } from 'react';
import { shippingAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Check, Star } from 'lucide-react';
import { toast } from 'sonner';

const ShippingZonesManager = () => {
  const [zones, setZones] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingZone, setEditingZone] = useState(null); // zone_key being edited
  const [zoneDraft, setZoneDraft] = useState({});
  const [showNewZone, setShowNewZone] = useState(false);
  const [newZone, setNewZone] = useState({ zone_key: '', label: '', fee: '', free_shipping_threshold: '' });

  const [showNewCity, setShowNewCity] = useState(false);
  const [newCity, setNewCity] = useState({ city: '', zone_key: '' });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [zonesData, citiesData] = await Promise.all([
        shippingAPI.adminGetZones(),
        shippingAPI.adminGetCities(),
      ]);
      setZones(zonesData);
      setCities(citiesData);
    } catch (error) {
      toast.error('Failed to load shipping zones');
    } finally {
      setLoading(false);
    }
  };

  const startEditZone = (zone) => {
    setEditingZone(zone.zone_key);
    setZoneDraft({ label: zone.label, fee: zone.fee, free_shipping_threshold: zone.free_shipping_threshold });
  };

  const saveZone = async (zoneKey) => {
    try {
      await shippingAPI.adminUpdateZone(zoneKey, zoneDraft);
      toast.success('Zone updated');
      setEditingZone(null);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update zone');
    }
  };

  const deleteZone = async (zoneKey) => {
    if (!window.confirm('Delete this shipping zone?')) return;
    try {
      await shippingAPI.adminDeleteZone(zoneKey);
      toast.success('Zone deleted');
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete zone');
    }
  };

  const setDefault = async (zoneKey) => {
    try {
      await shippingAPI.adminSetDefaultZone(zoneKey);
      toast.success('Default zone updated');
      loadAll();
    } catch (error) {
      toast.error('Failed to set default zone');
    }
  };

  const createZone = async () => {
    if (!newZone.zone_key || !newZone.label) {
      toast.error('Zone key and label are required');
      return;
    }
    try {
      await shippingAPI.adminCreateZone({
        zone_key: newZone.zone_key,
        label: newZone.label,
        fee: Number(newZone.fee) || 0,
        free_shipping_threshold: Number(newZone.free_shipping_threshold) || 0,
      });
      toast.success('Zone created');
      setShowNewZone(false);
      setNewZone({ zone_key: '', label: '', fee: '', free_shipping_threshold: '' });
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create zone');
    }
  };

  const addCity = async () => {
    if (!newCity.city || !newCity.zone_key) {
      toast.error('City name and zone are required');
      return;
    }
    try {
      await shippingAPI.adminAddCity(newCity.city, newCity.zone_key);
      toast.success('City added');
      setShowNewCity(false);
      setNewCity({ city: '', zone_key: '' });
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add city');
    }
  };

  const updateCityZone = async (city, zoneKey) => {
    try {
      await shippingAPI.adminUpdateCity(city, zoneKey);
      toast.success('City updated');
      loadAll();
    } catch (error) {
      toast.error('Failed to update city');
    }
  };

  const deleteCity = async (city) => {
    if (!window.confirm(`Remove ${city}? It will fall back to the default zone.`)) return;
    try {
      await shippingAPI.adminDeleteCity(city);
      toast.success('City removed');
      loadAll();
    } catch (error) {
      toast.error('Failed to remove city');
    }
  };

  if (loading) return <div className="skeleton h-64 rounded-2xl" />;

  return (
    <div className="space-y-8">
      {/* Zones */}
      <Card className="luxury-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Shipping Zones</CardTitle>
          <Button size="sm" onClick={() => setShowNewZone(true)} data-testid="add-zone-button">
            <Plus className="w-4 h-4 mr-1" /> Add Zone
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zone</TableHead>
                <TableHead>Fee (KES)</TableHead>
                <TableHead>Free shipping above (KES)</TableHead>
                <TableHead>Default</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {zones.map((zone) => (
                <TableRow key={zone.zone_key}>
                  <TableCell>
                    {editingZone === zone.zone_key ? (
                      <Input
                        value={zoneDraft.label}
                        onChange={(e) => setZoneDraft({ ...zoneDraft, label: e.target.value })}
                        className="w-40"
                      />
                    ) : (
                      <span className="font-medium">{zone.label}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingZone === zone.zone_key ? (
                      <Input
                        type="number"
                        value={zoneDraft.fee}
                        onChange={(e) => setZoneDraft({ ...zoneDraft, fee: e.target.value })}
                        className="w-24"
                      />
                    ) : (
                      zone.fee.toLocaleString()
                    )}
                  </TableCell>
                  <TableCell>
                    {editingZone === zone.zone_key ? (
                      <Input
                        type="number"
                        value={zoneDraft.free_shipping_threshold}
                        onChange={(e) => setZoneDraft({ ...zoneDraft, free_shipping_threshold: e.target.value })}
                        className="w-28"
                      />
                    ) : (
                      zone.free_shipping_threshold.toLocaleString()
                    )}
                  </TableCell>
                  <TableCell>
                    {zone.is_default ? (
                      <span className="flex items-center gap-1 text-xs text-accent"><Star className="w-3 h-3 fill-current" /> Default</span>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => setDefault(zone.zone_key)}>Set default</Button>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    {editingZone === zone.zone_key ? (
                      <Button size="sm" onClick={() => saveZone(zone.zone_key)} data-testid={`save-zone-${zone.zone_key}`}>
                        <Check className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => startEditZone(zone)} data-testid={`edit-zone-${zone.zone_key}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => deleteZone(zone.zone_key)} disabled={zone.is_default} data-testid={`delete-zone-${zone.zone_key}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Cities */}
      <Card className="luxury-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Cities</CardTitle>
          <Button size="sm" onClick={() => setShowNewCity(true)} data-testid="add-city-button">
            <Plus className="w-4 h-4 mr-1" /> Add City
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>City</TableHead>
                <TableHead>Zone</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cities.map((c) => (
                <TableRow key={c.city}>
                  <TableCell className="font-medium">{c.city}</TableCell>
                  <TableCell>
                    <Select value={c.zone_key} onValueChange={(v) => updateCityZone(c.city, v)}>
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {zones.map((z) => (
                          <SelectItem key={z.zone_key} value={z.zone_key}>{z.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => deleteCity(c.city)} data-testid={`delete-city-${c.city}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* New Zone Dialog */}
      <Dialog open={showNewZone} onOpenChange={setShowNewZone}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Shipping Zone</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Zone key (internal, no spaces)</Label>
              <Input
                value={newZone.zone_key}
                onChange={(e) => setNewZone({ ...newZone, zone_key: e.target.value })}
                placeholder="e.g. north_coast"
              />
            </div>
            <div>
              <Label>Label (shown to customers if needed)</Label>
              <Input
                value={newZone.label}
                onChange={(e) => setNewZone({ ...newZone, label: e.target.value })}
                placeholder="e.g. North Coast"
              />
            </div>
            <div>
              <Label>Shipping fee (KES)</Label>
              <Input type="number" value={newZone.fee} onChange={(e) => setNewZone({ ...newZone, fee: e.target.value })} />
            </div>
            <div>
              <Label>Free shipping above (KES)</Label>
              <Input
                type="number"
                value={newZone.free_shipping_threshold}
                onChange={(e) => setNewZone({ ...newZone, free_shipping_threshold: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewZone(false)}>Cancel</Button>
            <Button onClick={createZone}>Create Zone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New City Dialog */}
      <Dialog open={showNewCity} onOpenChange={setShowNewCity}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add City</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>City name</Label>
              <Input value={newCity.city} onChange={(e) => setNewCity({ ...newCity, city: e.target.value })} placeholder="e.g. Voi" />
            </div>
            <div>
              <Label>Zone</Label>
              <Select value={newCity.zone_key} onValueChange={(v) => setNewCity({ ...newCity, zone_key: v })}>
                <SelectTrigger><SelectValue placeholder="Select a zone" /></SelectTrigger>
                <SelectContent>
                  {zones.map((z) => (
                    <SelectItem key={z.zone_key} value={z.zone_key}>{z.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCity(false)}>Cancel</Button>
            <Button onClick={addCity}>Add City</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShippingZonesManager;
